// Module 09 §8 — 09 registers itself as the writer for 07's `dashboard_access` bootstrap section
// (contract 07 §3, ledger A11). This is the PRIMARY definitions source; the store's per-workspace
// fan-out is the explicit migration fallback for a response that carries no such section.
//
// `dashboard_access` (BE-ACC-05) is one user-scoped payload listing every dashboard the caller
// can see across ALL visible workspaces — it exists to replace exactly the N+1 that fan-out is.
//
// WHAT THIS DELIBERATELY DOES NOT DO: write `workspace_attributes` / `workspace_integrations`.
// They ride the same section, and v3's store wrote them straight into `workspaceStore`
// (`dashboardStore.ts:119-120`) — a dashboard READ mutating another feature's state, which is
// the island violation this registry exists to remove. 07 already maps both payloads itself
// (`BootstrapMapper.ts:286-287`) and owns routing them to their feature; 09 passes on them.
import type { DashboardDefinitionDto } from '../models/api.types'
import { useDashboardStore } from '../stores/dashboardStore'

import {
  registerBootstrapHydrator,
  type DashboardAccessSection,
  type MappedBootstrapResult,
} from '@/features/account'

/**
 * `CustomDashboardItem` (07's wire shape) → `DashboardDefinitionDto` (09's). The two agree on
 * every field 09 reads; the cast is narrowing `sections: unknown[] | null` to the parsed wire
 * type, which `parseLayout` guards at every level anyway.
 */
function toDefinitions(section: DashboardAccessSection): DashboardDefinitionDto[] {
  return (section.dashboards ?? []).map(
    (item) =>
      ({
        ...item,
        id: Number(item.id),
        version: item.version ?? 0,
      }) as unknown as DashboardDefinitionDto,
  )
}

function hydrateDashboardAccess(result: MappedBootstrapResult): void {
  // `null` means the section failed or came back cold: keep the warm list rather than blanking
  // the sidebar (the same rule 08's and 12's hydrators follow). An empty array is explicit.
  if (result.dashboardAccess === null) return
  useDashboardStore().setDefinitions(toDefinitions(result.dashboardAccess))
  if (Array.isArray(result.accountCenters)) {
    useDashboardStore().setAccountCenters(
      result.accountCenters as unknown as DashboardDefinitionDto[],
    )
  }
}

let unregister: (() => void) | null = null

/** Register module 09 as the owner of bootstrap dashboard access. Idempotent. */
export function registerDashboardBootstrapHydrator(): () => void {
  unregister?.()
  const dispose = registerBootstrapHydrator('dashboard_access', {
    hydrate: hydrateDashboardAccess,
    // An account switch must drop the prior tenant's dashboards before the new payload lands —
    // a stale definition here is a link to another account's dashboard in the sidebar.
    reset: () => useDashboardStore().resetState(),
  })
  unregister = () => {
    dispose()
    unregister = null
  }
  return unregister
}
